/**
 * Walk a dragged-in folder using the non-standard webkitGetAsEntry
 * API and produce a flat list of files with their relative paths
 * (paths inside the dropped folder, leading segment of the dropped
 * folder itself stripped).
 */
export interface DroppedFile {
  file: File;
  /** Path relative to the dropped folder root, e.g. "title-a/video/feature.mov". */
  relativePath: string;
}

export async function readDroppedItems(items: DataTransferItemList): Promise<DroppedFile[]> {
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = items[i]!.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  if (entries.length === 0) return [];

  // If exactly one folder was dropped, treat the contents of that
  // folder as the batch root. If multiple things were dropped, treat
  // them as siblings under a virtual root (they probably are).
  if (entries.length === 1 && entries[0]!.isDirectory) {
    return walkDirectory(entries[0] as FileSystemDirectoryEntry, '');
  }
  const out: DroppedFile[] = [];
  for (const entry of entries) {
    if (entry.isFile) {
      out.push({ file: await fileFrom(entry as FileSystemFileEntry), relativePath: entry.name });
    } else {
      out.push(...(await walkDirectory(entry as FileSystemDirectoryEntry, entry.name)));
    }
  }
  return out;
}

async function walkDirectory(
  dir: FileSystemDirectoryEntry,
  prefix: string
): Promise<DroppedFile[]> {
  const reader = dir.createReader();
  const all: FileSystemEntry[] = [];
  // readEntries returns at most ~100 at a time; loop until empty.
  while (true) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject)
    );
    if (batch.length === 0) break;
    all.push(...batch);
  }
  const out: DroppedFile[] = [];
  for (const entry of all) {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.isFile) {
      out.push({ file: await fileFrom(entry as FileSystemFileEntry), relativePath: path });
    } else {
      out.push(...(await walkDirectory(entry as FileSystemDirectoryEntry, path)));
    }
  }
  return out;
}

function fileFrom(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * Categorize each dropped file into the upload metadata it needs.
 * Files that don't fit the standard convention are accepted as raw_file.
 */
export type Categorized =
  | { kind: 'metadata_csv'; file: File; relativePath: string }
  | {
      kind: 'asset';
      file: File;
      relativePath: string;
      providerId: string;
      subdir: 'video' | 'audio' | 'subtitles' | 'artwork';
      filename: string;
    }
  | { kind: 'raw_file'; file: File; relativePath: string };

const ALLOWED_SUBDIRS = new Set(['video', 'audio', 'subtitles', 'artwork']);
const PROVIDER_ID = /^[a-zA-Z0-9_-]{1,64}$/;

export function categorize(files: DroppedFile[]): Categorized[] {
  return files.map((f) => {
    const segments = f.relativePath.split('/').filter(Boolean);
    if (segments.length === 1 && segments[0]!.toLowerCase() === 'metadata.csv') {
      return { kind: 'metadata_csv', file: f.file, relativePath: f.relativePath };
    }
    if (segments.length === 3) {
      const [providerId, subdir, filename] = segments as [string, string, string];
      if (PROVIDER_ID.test(providerId) && ALLOWED_SUBDIRS.has(subdir)) {
        return {
          kind: 'asset',
          file: f.file,
          relativePath: f.relativePath,
          providerId,
          subdir: subdir as 'video' | 'audio' | 'subtitles' | 'artwork',
          filename,
        };
      }
    }
    // Accept any file that doesn't match standard structure
    return { kind: 'raw_file', file: f.file, relativePath: f.relativePath };
  });
}

declare global {
  interface DataTransferItem {
    webkitGetAsEntry(): FileSystemEntry | null;
  }
}
